import { useQueries } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { agentServiceQueryKey } from "@/queries/agent-queries";
import { useCreateEnvironmentLogsToken } from "@/queries/environment-queries";

export type BundleTokenOptions = {
    /** Environment ID for the environment logs token. */
    environmentId?: string;
    /** Agent execution IDs to fetch conversation tokens for. */
    executionIds: string[];
};

export type BundleTokenResult = {
    /** Call this to start fetching tokens (deferred loading pattern). */
    enableTokenFetch: () => void;
    /** True when all requested tokens are available. */
    isTokenReady: boolean;
    /** The environment logs token, or undefined if not yet fetched. */
    environmentLogsToken: string | undefined;
    /** Map of executionId → conversation token. Only includes entries where the token is available. */
    executionTokens: Map<string, string>;
};

const TOKEN_VALID_DURATION = 1000 * 60 * 45; // 45 minutes

export const useBundleTokens = (options: BundleTokenOptions): BundleTokenResult => {
    const { environmentId, executionIds } = options;
    const api = useGitpodAPI();

    const [tokenEnabled, setTokenEnabled] = useState(false);

    const enableTokenFetch = useCallback(() => {
        setTokenEnabled(true);
    }, []);

    // Environment logs token — deferred via the same pattern as useEnvironmentSupportBundle.
    const { data: environmentLogsToken } = useCreateEnvironmentLogsToken(
        tokenEnabled && environmentId ? environmentId : undefined,
    );

    // Execution conversation tokens — use useQueries to handle a dynamic list of IDs.
    const executionTokenResults = useQueries({
        queries: executionIds.map((executionId) => ({
            queryKey: agentServiceQueryKey.createAgentExecutionConversationToken(executionId),
            queryFn: async (): Promise<string> => {
                const { token } = await api.agentService.createAgentExecutionConversationToken({
                    agentExecutionId: executionId,
                });
                if (!token) {
                    throw new Error("Failed to create token");
                }
                return token;
            },
            staleTime: TOKEN_VALID_DURATION,
            gcTime: TOKEN_VALID_DURATION,
            enabled: tokenEnabled,
        })),
    });

    // Extract stable data from useQueries results to avoid passing the unstable result array into deps.
    const executionTokenData = executionTokenResults.map((r) => r.data);

    const executionTokens = useMemo(() => {
        const map = new Map<string, string>();
        for (let i = 0; i < executionIds.length; i++) {
            const token = executionTokenData[i];
            if (token) {
                map.set(executionIds[i], token);
            }
        }
        return map;
    }, [executionIds, executionTokenData]);

    const isTokenReady = useMemo(() => {
        const needsEnvToken = !!environmentId;
        const needsExecTokens = executionIds.length > 0;

        if (!needsEnvToken && !needsExecTokens) {
            return true;
        }

        if (needsEnvToken && !environmentLogsToken) {
            return false;
        }

        if (needsExecTokens && executionTokens.size < executionIds.length) {
            return false;
        }

        return true;
    }, [environmentId, environmentLogsToken, executionIds.length, executionTokens.size]);

    return {
        enableTokenFetch,
        isTokenReady,
        environmentLogsToken,
        executionTokens,
    };
};
